import type { MeResponse } from "@ondo/shared";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import {
  acceptSeatInvitation,
  declineSeatInvitation,
  fetchSeatInvitations,
} from "../lib/api.ts";
import { describeApiError } from "../lib/apiError.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { workspacePath } from "../routes/routes.model.ts";
import { SEAT_INVITATION_COPY } from "./workspace.constants.ts";
import { describeSeatInvitations, seatWorkspaceFor } from "./workspace.model.ts";
import type { SeatInvitationNote } from "./workspace.model.ts";

/**
 * The invitations waiting at this account's own address — OND-24.
 *
 * `useClaimStudioController`'s shape, deliberately, because it is the same kind
 * of thing one ticket along: somebody who was reached by email is told on the
 * first screen after they are in, rather than having to go looking. Two
 * differences, and both are about the subject rather than the mechanism.
 *
 * A LIST RATHER THAN ONE OFFER. Two firms may have invited the same person and
 * neither should hide the other, so this holds an array and the view draws one
 * block per invitation.
 *
 * TWO ANSWERS RATHER THAN ONE. A dormant studio can only be claimed — declining
 * it would mean refusing to be yourself — but an invitation to somebody else's
 * firm has a real no in it, and `20261012096500` decision 2 is why saying no is
 * worth a button: "a decline is not a suppression", so the firm may ask again
 * and the person is not left with a banner they cannot clear.
 *
 * THE RESPONSE IS THE READ, on both. `POST /me/seat-invitations/:id/accept`
 * answers with the whole `MeResponse` because a seat moves `capacities`, so
 * `replaceMe` puts it where every screen looks and the workspaces re-derive on
 * the same render. The controller never decides that the account now holds a
 * seat; it asks, and goes where the answer points.
 */
export type SeatInvitationsView =
  | { kind: "hidden" }
  | {
      kind: "offered";
      notes: SeatInvitationNote[];
      /** The invitation a request is in flight for, so one block disables alone. */
      busy: string | null;
      error: string | null;
    };

export function useSeatInvitationsController(input: {
  replaceMe: (me: MeResponse) => void;
}): {
  view: SeatInvitationsView;
  accept: (invitationId: string) => void;
  decline: (invitationId: string) => void;
} {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<SeatInvitationNote[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    fetchSeatInvitations()
      .then((answer) => {
        if (active) setNotes(describeSeatInvitations(answer.invitations));
      })
      .catch(() => {
        // A failed read draws nothing, which is `useClaimStudioController`'s
        // rule and its reason: the banner is an offer, and an offer that cannot
        // be confirmed is better absent than guessed at.
        if (active) setNotes([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const view: SeatInvitationsView =
    notes.length > 0 ? { kind: "offered", notes, busy, error } : { kind: "hidden" };

  /**
   * Finds the note so the acceptance knows which firm to land on, and so a
   * second press while the first is in flight does nothing.
   *
   * `busy` IS THE INVITATION'S ID AND NOT A BOOLEAN, because two invitations may
   * be on screen: a boolean would disable both blocks on the first press, and a
   * reader who meant to accept one and decline the other would have to wait
   * without being told why.
   */
  const noteFor = (invitationId: string) =>
    busy === null ? notes.find((note) => note.invitationId === invitationId) : undefined;

  return {
    view,

    accept: (invitationId: string) => {
      const note = noteFor(invitationId);
      if (!note) return;

      setBusy(invitationId);
      setError(null);

      acceptSeatInvitation(invitationId)
        .then((me) => {
          input.replaceMe(me);
          // Found in the profile the server just returned, by the firm's own
          // ONDO id rather than by role — the account may already hold a
          // workspace of the same kind, and this must land on the firm it just
          // joined. The chooser is a true answer when the profile does not name
          // it rather than a fallback to guessing.
          const workspace = seatWorkspaceFor(me, note.orgOndoId);
          void navigate(
            workspace
              ? workspacePath(workspace, SEAT_INVITATION_COPY.landing)
              : PATHS.workspaces,
            { replace: true },
          );
        })
        .catch((cause: unknown) => {
          setError(describeApiError(cause, SEAT_INVITATION_COPY.acceptFailed));
        })
        .finally(() => setBusy(null));
    },

    decline: (invitationId: string) => {
      const note = noteFor(invitationId);
      if (!note) return;

      setBusy(invitationId);
      setError(null);

      declineSeatInvitation(invitationId)
        .then((me) => {
          // NO NAVIGATION, which is the one place this differs from accepting
          // beyond the route it calls: declining opens nothing, so the reader
          // stays where they are and the banner's own list is what changes.
          input.replaceMe(me);
          setNotes((current) =>
            current.filter((candidate) => candidate.invitationId !== invitationId),
          );
        })
        .catch((cause: unknown) => {
          setError(describeApiError(cause, SEAT_INVITATION_COPY.declineFailed));
        })
        .finally(() => setBusy(null));
    },
  };
}
